import React, { useEffect, useState } from 'react';
import { FormControl, FormLabel } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { useObjectContext } from '@/hooks/useObjectContext';
import { showToast } from '@/features/toast/toastSlice';
import { useMSALHelper } from '@/hooks/useMSALHelper';
import { getFolderFromPipeline } from '@/utils/constants';

import { useGetDealsByClientIdQuery, useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import {
	useUpdateClientRowMutation,
	useUpdateClientxMutation,
} from '@/features/clients/clientsApi';
import {
	useCopySharepointItemMutation,
	useAddFolderToSharepointMutation,
} from '@/features/msGraph/msGraphApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';

export const ChangeAssigneeField = ({
	companySalesTeamChange = false,
	disabled,
	minimal = true,
}) => {
	const { dealRow, clientRow, insight, dealId, broker } = useObjectContext();
	const docId = clientRow?.docId;
	const clientId = clientRow?.clientId;

	const [updateClientx, { isLoading: isUpdatingClientInsight }] = useUpdateClientxMutation();
	const [copySharepontItem] = useCopySharepointItemMutation();
	const [updateDealRow, { isLoading: isUpdatingDealRow }] = useUpdateDealRowMutation();
	const [updateClientRow, { isLoading: isUpdatingClientRow }] = useUpdateClientRowMutation();

	const [updatingSalesTeam, setUpdatingSalesTeam] = useState(false);

	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { getAccessToken } = useMSALHelper();
	const isDeal = dealRow?.objectType === 'deal';
	const isDebtor = dealRow?.objectType === 'debtor';

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const isLoading = isUpdatingClientInsight || isUpdatingDealRow || isUpdatingClientRow;

	const initialOwnerDocId = dealRow?.ownerDocId || clientRow?.ownerDocId || userDetails?.userId;

	const { currentData: deals = [], ...dealsPropertiesQuery } = useGetDealsByClientIdQuery(
		clientRow?.clientId,
		{
			skip: !clientRow?.clientId,
		},
	);

	const [showDialog, setShowDialog] = useState(false);
	const [userPromptedData, setUserPromptedData] = useState(null);

	const copyFoldersMigration = async (value) => {
		setUpdatingSalesTeam(true);
		try {
			const copyRes = await copySharepontItem({
				sourceDriveId: clientRow?.clientFolderDriveId,
				itemId: clientRow?.driveId,
				targetDriveId: value?.brokerGroupData?.clientFolderDriveId,
				newName: clientRow?.name,
				accessToken: await getAccessToken(),
			}).unwrap();
			const clientFolder = copyRes?.items.find(
				(item) =>
					item.type === 'folder' &&
					item.name &&
					item.name?.trim()?.toLowerCase() === clientRow?.name?.trim()?.toLowerCase(),
			);

			console.log('c folder: ', clientFolder);

			if (clientFolder) {
				const clientNoteFolder = copyRes?.items.find(
					(item) =>
						item.type === 'folder' &&
						item.parentId === clientFolder.id &&
						item.name?.toLowerCase()?.includes('client generated notes'),
				);

				const updateRes = await updateClientRow({
					clientId,
					docId,
					properties: {
						notesDriveId: clientNoteFolder?.id,
						driveId: clientFolder.id,
						clientFolderDriveId: value?.brokerGroupData?.clientFolderDriveId,
					},
				}).unwrap();
				console.log('updateClientRow response:', updateRes);
				for (const deal of deals) {
					const dealFolder = copyRes?.data?.items.find(
						(item) =>
							item.type === 'folder' &&
							item.name === deal.dealName &&
							item.parentFolderName === getFolderFromPipeline(deal?.pipeline),
					);
					if (dealFolder) {
						const noteFolder = copyRes?.data?.items.find(
							(item) =>
								item.type === 'folder' &&
								item.parentId === dealFolder.id &&
								item.name?.toLowerCase()?.includes('notes'),
						);
						console.log('Deal folder:', dealFolder);
						console.log('Note folder:', noteFolder);

						await updateDealRow({
							dealId: deal?.dealId ?? dealRow?.dealId,
							docId: deal?.id ?? deal.docId,
							properties: {
								driveId: dealFolder?.id,
								notesDriveId: noteFolder?.id,
								clientFolderDriveId: value?.brokerGroupData?.clientFolderDriveId,
							},
						}).unwrap();
					}
				}
				await updateClientData(userPromptedData);
			}
		} catch (err) {
			console.error('👾 - Error copying folders or updating properties:', err);
			dispatch(
				showToast({
					message: `Error during sales team migration. ${err.message || err.error || ''}`,
					error: true,
				}),
			);
		} finally {
			setUserPromptedData(null);
			setUpdatingSalesTeam(false);
		}
	};

	const updateClientData = async (value) => {
		const prevSalesTeamId = insight?.SalesTeamId;
		const prevClientOwnerDocId = clientRow?.ownerDocId ?? null;
		try {
			await updateClientx({
				clientId,
				insight: { SalesTeamId: value?.salesTeamId },
				firebase: { ownerDocId: value?.id },
			}).unwrap();
		} catch (err) {
			console.log('Error updating client data undo...', err);
			try {
				await updateClientx({
					clientId,
					insight: { SalesTeamId: prevSalesTeamId },
					firebase: { ownerDocId: prevClientOwnerDocId },
				}).unwrap();
			} catch (err) {
				console.log('error updating client:', err);
			}
			dispatch(
				showToast({
					message: `Error updating client: ${err.data.message} `,
					error: true,
				}),
			);
		}
	};

	const handleAssigneeChange = async (value) => {
		console.log('values: ', value);
		console.log('client data: ', clientRow);

		const ownerDocId = value?.id ?? null;

		try {
			if (companySalesTeamChange && clientRow) {
				//If the assignees clientFolderDriveId is different prompt user otherwise update everything expect sharepoint..
				if (broker?.clientFolderDriveId !== value?.brokerGroupData?.clientFolderDriveId) {
					setUserPromptedData(value);
					setShowDialog(true);
				} else {
					updateClientData(value);
				}
			} else {
				await updateDealRow({
					dealId: dealId ?? dealRow?.dealId,
					docId: dealRow?.id,
					properties: {
						...(ownerDocId && { ownerDocId }),
					},
				}).unwrap();
			}
		} catch (err) {
			console.error('Error in handleAssigneeChange:', err);
			dispatch(
				showToast({
					message: `Error updating client: ${err}`,
					error: true,
				}),
			);
		}
	};

	const handleConfirm = async () => {
		if (userPromptedData) {
			setShowDialog(false);
			await copyFoldersMigration(userPromptedData);
		}
		setShowDialog(false);
	};

	const handleClose = () => {
		setShowDialog(false);
		setUserPromptedData(null);
	};

	const isSelectDisabled =
		(isDeal && (!initialOwnerDocId || isUpdatingDealRow)) ||
		(companySalesTeamChange && !userDetails?.principleUser) ||
		isLoading ||
		showDialog ||
		disabled ||
		updatingSalesTeam;

	return (
		<>
			<FormControl fullWidth>
				<FormLabel>
					{companySalesTeamChange
						? 'Client owner'
						: isDebtor
							? 'Debtor assignee'
							: 'Deal assignee'}
				</FormLabel>
				<OwnerSelect
					minimal={minimal}
					initialId={initialOwnerDocId}
					{...(companySalesTeamChange && {
						filterBy: (user) => Boolean(user.salesTeamId),
					})}
					showGroup={true}
					disabled={isSelectDisabled}
					onChange={handleAssigneeChange}
				/>
			</FormControl>
			{showDialog && (
				<ConfirmDialog
					openDialog={showDialog}
					handleClose={handleClose}
					handleConfirm={handleConfirm}
					actionName={''}
					title={'Client folder migration'}
					additionalContent={
						'By confirming we will create the client folder structure in the new sharepoint location. All other created file/folders will have to be manually transfered over.'
					}
					canBeUndone={true}
				/>
			)}
		</>
	);
};
